/* ============================================================================
 * hfb-motion.js — the ONE reveal chokepoint for a bespoke Horizon theme.
 *
 * Grafted at provision, labelled `origin: hfb`, never refreshed by a Horizon
 * upgrade. Its twin is assets/hfb-motion.css; the consumer is
 * snippets/hfb-reveal.liquid.
 *
 * THERE IS EXACTLY ONE OBSERVER AND ONE STATE CLASS (`is-revealed`), site-wide.
 * A second reveal mechanism authored inside a section is the failure this file
 * exists to prevent: per-section motion drifts within a single build and cannot
 * be retuned without touching every file. craft-floor-check.mjs (M3) fails a
 * theme that grows one. (It deliberately does NOT object to other state classes
 * — a carousel's `.is-active` is legitimate and unrelated.)
 *
 * READINESS IS THE SAFETY CONTRACT, AND THE ORDER MATTERS.
 * The CSS hides nothing until <html> carries `hfb-motion-ready`. This file adds
 * that class ONLY after the observer exists AND every reveal element has been
 * registered, and removes it again on any failure. So a partial init cannot
 * leave a subset of elements hidden with nothing watching them — which is the
 * precise way a "safe" reveal system ships a blank page.
 *
 * A second, independent block at the END of this file animates FAQ rows
 * (<details data-hfb-disclosure>) open and closed. It shares nothing with the
 * reveal code and does not depend on it having run.
 * ========================================================================== */
(function () {
  'use strict';

  var READY = 'hfb-motion-ready';
  var root = document.documentElement;
  // SHOWCASE TIER ONLY (<html data-motion-tier="showcase">, set by execute-theme on a
  // Showcase build). Every branch below that reads SHOWCASE is inert on any other tier, so
  // a Signature/Baseline/Static theme behaves exactly as it did before this was added.
  var SHOWCASE = root.getAttribute('data-motion-tier') === 'showcase';
  var SELECTOR = '.hfb-reveal, .hfb-reveal-stagger, .hfb-clip-reveal' +
    (SHOWCASE ? ', .hfb-sc-img, [data-hfb-sc-reveal="line-mask"]' : '');
  var SC_IMAGE_WAIT_MS = 4000; // a photo slower than this fades in plainly (never pops)
  var SC_GROUP_CAP_S = 1.2;    // a stagger group never takes longer than this to start its last item

  function revealAll(nodes) {
    for (var i = 0; i < nodes.length; i++) nodes[i].classList.add('is-revealed');
  }

  /* --- Showcase image reveals: play only once the photo has loaded ----------
   * A photo that arrives late still gets its full reveal when it lands. One that
   * fails, or is slower than SC_IMAGE_WAIT_MS, gets data-hfb-sc-state="plain"
   * (invisible, no wipe) and then "shown" (a plain fade) — it never pops in, and
   * a timer guarantees it is never left invisible. */
  function scWhenReady(img, done) {
    var settled = false;
    function finish(ok) { if (!settled) { settled = true; done(ok); } }
    function decode() {
      if (img.decode) img.decode().then(function () { finish(true); }, function () { finish(img.naturalWidth > 0); });
      else finish(img.naturalWidth > 0);
    }
    if (!img) return finish(false);
    // `complete` with no pixels = it already FAILED: settle now, not after the timeout
    if (img.complete) { if (img.naturalWidth > 0) decode(); else finish(false); }
    else {
      img.addEventListener('load', decode);
      img.addEventListener('error', function () { finish(false); });
    }
    setTimeout(function () { finish(false); }, SC_IMAGE_WAIT_MS);
  }
  function scShowPlain(frame, img) {
    frame.setAttribute('data-hfb-sc-state', 'plain');
    var shown = false;
    function show() {
      if (shown) return;
      shown = true;
      requestAnimationFrame(function () { requestAnimationFrame(function () { frame.setAttribute('data-hfb-sc-state', 'shown'); }); });
    }
    // `complete` is also true for a FAILED image — show it, never wait for an event that fired
    if (!img || img.complete) return show();
    img.addEventListener('load', show);
    img.addEventListener('error', show);
    setTimeout(show, SC_IMAGE_WAIT_MS);
  }
  function scSettleImage(frame) {
    var img = frame.querySelector('img');
    // Chrome never starts a lazy <img> while it is fully clipped (the hidden reveal state),
    // so a lazy photo would not load until something unclipped it. Fetch it now.
    if (img && img.loading === 'lazy') img.loading = 'eager';
    scWhenReady(img, function (ok) {
      if (ok) frame.classList.add('is-revealed');
      else scShowPlain(frame, img);
    });
  }
  /* --- Showcase group stagger: 0 for one item, else min(stagger, 1.2s / (n - 1)) */
  function scSpaceGroups() {
    var raw = getComputedStyle(root).getPropertyValue('--hfb-sc-stagger') || '';
    var m = /([\d.]+)\s*(ms|s)/.exec(raw);
    var stagger = m ? (m[2] === 'ms' ? parseFloat(m[1]) / 1000 : parseFloat(m[1])) : 0.22;
    var hosts = document.querySelectorAll('.hfb-reveal-stagger');
    for (var h = 0; h < hosts.length; h++) {
      var kids = hosts[h].children;
      var n = kids.length;
      var spacing = n > 1 ? Math.min(stagger, SC_GROUP_CAP_S / (n - 1)) : 0;
      for (var k = 0; k < n; k++) kids[k].style.setProperty('--hfb-reveal-delay', Math.round(k * spacing * 1000) + 'ms');
    }
  }

  /* --- Task 12483 part 2: grouped content staggers ~100ms per item, every tier ---------
   * Horizon's product grids join the stagger vocabulary (tagged here so no Horizon file is
   * edited), and each stagger group's items get a 100ms step (capped) unless an item
   * already carries its own delay. Showcase keeps its own spacing (scSpaceGroups). */
  var GROUP_STEP_MS = 100;
  var GROUP_CAP_MS = 800;
  function tagGroups() {
    var grids = document.querySelectorAll('.product-grid, .resource-list');
    for (var g = 0; g < grids.length; g++) grids[g].classList.add('hfb-reveal-stagger');
  }
  function spaceGroups() {
    var hosts = document.querySelectorAll('.hfb-reveal-stagger');
    for (var h = 0; h < hosts.length; h++) {
      var kids = hosts[h].children;
      for (var k = 0; k < kids.length; k++) {
        if (kids[k].style.getPropertyValue('--hfb-reveal-delay')) continue;
        kids[k].style.setProperty('--hfb-reveal-delay', Math.min(k * GROUP_STEP_MS, GROUP_CAP_MS) + 'ms');
      }
    }
  }

  function init() {
    try { tagGroups(); if (!SHOWCASE) spaceGroups(); } catch (e) { /* groups simply animate together */ }
    var nodes = document.querySelectorAll(SELECTOR);

    // No IntersectionObserver (or no elements): never arm the hiding CSS. The
    // page renders final-state. This is a supported outcome, not a fallback
    // failure — the class is simply never added.
    if (!('IntersectionObserver' in window)) {
      root.classList.remove(READY);
      revealAll(nodes);
      return;
    }
    if (!nodes.length) return;

    var observer;
    try {
      observer = new IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          if (!entries[i].isIntersecting) continue;
          var target = entries[i].target;
          observer.unobserve(target);
          // Showcase image frames reveal only once their photo has loaded.
          if (SHOWCASE && target.classList.contains('hfb-sc-img')) { scSettleImage(target); continue; }
          target.classList.add('is-revealed');
        }
      }, SHOWCASE
        // Showcase: start the moment an element enters the screen.
        ? { rootMargin: '0px', threshold: 0 }
        : { rootMargin: '0px 0px -10% 0px', threshold: 0.01 });


      if (SHOWCASE) {
        scSpaceGroups();
        // Frames already on screen at start show as-is — no hide-then-reveal flash.
        var vh = window.innerHeight || 0;
        for (var j = 0; j < nodes.length; j++) {
          if (!nodes[j].classList.contains('hfb-sc-img')) continue;
          var r = nodes[j].getBoundingClientRect();
          if (r.bottom > 0 && r.top < vh) nodes[j].setAttribute('data-hfb-sc-state', 'done');
        }
      }

      // Register EVERY element before arming the CSS. If any observe() throws,
      // the catch below un-arms and reveals — we never half-hide a page.
      // (Showcase: a frame already on screen is final — `done` — and is never observed, or
      // the load-wait could fade a slow, already-visible photo OUT.)
      for (var i = 0; i < nodes.length; i++) {
        if (SHOWCASE && nodes[i].getAttribute('data-hfb-sc-state') === 'done') continue;
        observer.observe(nodes[i]);
      }
    } catch (e) {
      root.classList.remove(READY);
      revealAll(nodes);
      if (observer && observer.disconnect) observer.disconnect();
      return;
    }

    // Registration complete — only now is it safe for the CSS to hide anything.
    root.classList.add(READY);

    // Anything already in view at load reveals on the observer's first callback,
    // which fires immediately; no separate above-the-fold sweep is needed.
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

/* ============================================================================
 * Disclosures — FAQ rows open and close smoothly.
 *
 * A native <details> snaps: a browser cannot transition to `height: auto`, and
 * the CSS that can (`interpolate-size`) is Chromium-only. This animates the
 * answer with the Web Animations API instead, so it is the same in every browser.
 *
 * THE CONTRACT (execute-theme.md, "Motion"):
 *   <details data-hfb-disclosure>
 *     <summary>question</summary>
 *     <div>answer — exactly ONE element after the summary</div>
 *   </details>
 * That is the whole of it. Design, icon, and how many rows open at once are the
 * section's own business. A row without the attribute is never touched.
 *
 * While a row is moving it carries data-hfb-disclosure-state="opening|closing"
 * (removed when it settles). The CSS clips the answer off it, and a section may
 * key its icon off it: `details[open]:not([data-hfb-disclosure-state="closing"])`
 * turns the icon back as the close STARTS, not when it ends.
 *
 * NATIVE GROUPS (<details name="x">). The browser closes the open peer the
 * instant another row opens, so there is nothing left to animate afterwards.
 * The click is taken over and run in sequence: animate the open peer closed,
 * remove its `open`, then open and animate the clicked row. The `name` is never
 * touched, so the group is a valid one-open group at every step.
 *
 * IT CAN ONLY EVER FALL BACK TO NATIVE. This block is deliberately separate
 * from the reveal code above (which returns early on a page with no reveals):
 * it hides nothing, writes no inline style, and on reduced motion, a missing
 * Web Animations API, a malformed row, or any thrown error the row simply
 * toggles the way a plain <details> does.
 * ========================================================================== */
(function () {
  'use strict';

  var ATTR = 'data-hfb-disclosure';
  var STATE = 'data-hfb-disclosure-state';
  var DURATION_MS = 320;                         // --hfb-disclosure-duration
  var EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';     // --hfb-disclosure-ease
  var SEQUENCE_CLOSE = 0.6; // in a grouped sequence the closing leg is shorter, so the pair stays quick
  var PROPS = ['height', 'paddingTop', 'paddingBottom', 'marginTop', 'marginBottom', 'opacity'];
  var CLOSED = { height: '0px', paddingTop: '0px', paddingBottom: '0px', marginTop: '0px', marginBottom: '0px', opacity: '0' };
  var pending = {}; // group name -> the row waiting for its peers to finish closing

  if (!document.addEventListener) return;
  var reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function summaryOf(d) {
    for (var c = d.firstElementChild; c; c = c.nextElementSibling) if (c.tagName === 'SUMMARY') return c;
    return null;
  }
  /* The answer wrapper: the ONE element child that is not the summary. Anything else
   * (none, or several) is outside the contract and the row stays native. */
  function bodyOf(d) {
    var found = null;
    for (var c = d.firstElementChild; c; c = c.nextElementSibling) {
      if (c.tagName === 'SUMMARY') continue;
      if (found) return null;
      found = c;
    }
    return found;
  }
  function canAnimate(d) {
    if (!d.hasAttribute(ATTR)) return false;
    if (reduce && reduce.matches) return false;
    var body = bodyOf(d);
    return !!body && typeof body.animate === 'function';
  }
  function snapshot(el) {
    var cs = getComputedStyle(el);
    var out = {};
    for (var i = 0; i < PROPS.length; i++) out[PROPS[i]] = cs[PROPS[i]];
    return out;
  }
  function duration(d) {
    var m = /^\s*([\d.]+)\s*(ms|s)\s*$/.exec(getComputedStyle(d).getPropertyValue('--hfb-disclosure-duration') || '');
    if (!m) return DURATION_MS;
    var n = parseFloat(m[1]) * (m[2] === 's' ? 1000 : 1);
    return n >= 0 ? n : DURATION_MS;
  }
  function easing(d) {
    return (getComputedStyle(d).getPropertyValue('--hfb-disclosure-ease') || '').trim() || EASE;
  }
  /* What the row is heading towards — mid-animation that is not what `open` says. */
  function intent(d) {
    return d._hfbDisclosure ? d._hfbDisclosure.opening : d.open;
  }
  function openPeers(d, name) {
    var scope = d.getRootNode ? d.getRootNode() : document;
    var all = scope.querySelectorAll('details[open]');
    var out = [];
    for (var i = 0; i < all.length; i++) if (all[i] !== d && all[i].getAttribute('name') === name) out.push(all[i]);
    return out;
  }

  /* The single exit for every animation — finished, cancelled from outside, or never
   * started. Nothing transient survives it. */
  function settle(d, opening) {
    d._hfbDisclosure = null;
    d.removeAttribute(STATE);
    if (!opening) {
      d.open = false;
      afterClose(d.getAttribute('name'));
    }
  }

  function run(d, opening, ms) {
    var body = bodyOf(d);
    var prev = d._hfbDisclosure;
    // Reversing mid-flight starts from where the answer IS, not from an end state.
    var from = prev ? snapshot(body) : null;
    if (prev) { d._hfbDisclosure = null; prev.anim.cancel(); }
    if (opening && !d.open) d.open = true;
    var natural = snapshot(body);
    var anim;
    try {
      d.setAttribute(STATE, opening ? 'opening' : 'closing');
      anim = body.animate([from || (opening ? CLOSED : natural), opening ? natural : CLOSED],
        { duration: ms, easing: easing(d) });
    } catch (e) {
      settle(d, opening);
      return;
    }
    d._hfbDisclosure = { anim: anim, opening: opening };
    anim.onfinish = anim.oncancel = function () {
      if (!d._hfbDisclosure || d._hfbDisclosure.anim !== anim) return; // superseded by a newer run
      settle(d, opening);
    };
  }

  /* A grouped row waits here until every open peer has finished closing. */
  function afterClose(name) {
    if (!name || !pending[name]) return;
    var d = pending[name];
    if (openPeers(d, name).length) return;
    delete pending[name];
    if (canAnimate(d)) run(d, true, duration(d));
    else d.open = true;
  }

  function toggle(d) {
    var name = d.getAttribute('name');
    if (name && pending[name] === d) { delete pending[name]; return; } // changed their mind before it opened
    if (intent(d)) { run(d, false, duration(d)); return; }

    if (name) {
      delete pending[name];
      var peers = openPeers(d, name);
      if (peers.length) {
        pending[name] = d;
        for (var i = 0; i < peers.length; i++) {
          var p = peers[i];
          if (!canAnimate(p)) p.open = false;
          else if (intent(p)) run(p, false, duration(p) * SEQUENCE_CLOSE);
          // else: already closing — its own settle() calls afterClose
        }
        afterClose(name); // every peer closed synchronously
        return;
      }
    }
    run(d, true, duration(d));
  }

  document.addEventListener('click', function (e) {
    if (e.defaultPrevented) return;
    var t = e.target;
    var summary = t && t.closest ? t.closest('summary') : null;
    if (!summary) return;
    var d = summary.parentNode;
    if (!d || d.tagName !== 'DETAILS' || summaryOf(d) !== summary) return;
    // A link or control inside the question keeps its own behaviour.
    var inner = t.closest('a[href], button, input, select, textarea, label');
    if (inner && summary.contains(inner)) return;
    if (!canAnimate(d)) return;
    e.preventDefault();
    try { toggle(d); } catch (err) { d._hfbDisclosure = null; d.removeAttribute(STATE); d.open = !d.open; }
  });
})();
